import { CSSProperties, ReactNode } from "react";
import "./ObjectShadow.scss";

export type ShadowVariant =
  | "blue"
  | "orange"
  | "pink"
  | "purple"
  | "green"
  | "yellow"
  | "custom";

interface ObjectShadowProps {
  /** The room object (Lottie, image, etc) to render on top of the halo */
  children: ReactNode;

  /** Preset color theme — use this OR `color` */
  variant?: ShadowVariant;

  /** Explicit halo color (overrides variant) */
  color?: string;

  /** Halo width relative to the wrapper (e.g. "80%", "120px"). Default 90% */
  size?: string;

  /** Halo height relative to the wrapper. Default ~14% of width — keep elliptical */
  height?: string;

  /** Vertical offset of the halo from the bottom of the wrapper. Default -8% */
  offset?: string;

  /** Halo opacity (0–1). Default 0.55 */
  opacity?: number;

  /** Optional class on the outer wrapper */
  className?: string;

  /** Inline style on the outer wrapper — useful for positioning */
  style?: CSSProperties;

  /** Optional click handler — bubbles from the wrapper */
  onClick?: () => void;
}

/* Preset → color */
const VARIANT_COLORS: Record<Exclude<ShadowVariant, "custom">, string> = {
  blue:   "#5699E6",
  orange: "#FFB28A",
  pink:   "#F5B4D2",
  purple: "#C7A6F3",
  green:  "#A6E0AC",
  yellow: "#F9E08C",
};

/**
 * Drops a soft, colored elliptical halo *behind & below* the children.
 *
 * Used to make the interactive room objects (mic, headphones, painting easel,
 * sticker tray, etc.) "pop" out of the room background with a friendly glow,
 * mirroring the spotlight effect on the kids' rooms screens.
 *
 *   <ObjectShadow variant="blue">
 *     <button onClick={openChat}>
 *       <Lottie animationData={micIcon} />
 *     </button>
 *   </ObjectShadow>
 */
function ObjectShadow({
  children,
  variant = "blue",
  color,
  size = "100%",
  height,
  offset = "-8%",
  opacity = 1,
  className = "",
  style,
  onClick,
}: ObjectShadowProps) {
  const haloColor =
    color ?? (variant !== "custom" ? VARIANT_COLORS[variant] : VARIANT_COLORS.blue);

  // Default ellipse height ≈ 18% of wrapper height (caller can override)
  const haloHeight = height ?? "20%";

  const haloStyle: CSSProperties = {
    width: size,
    height: haloHeight,
    bottom: offset,
    background: haloColor,
    opacity,
  };

  return (
    <span
      className={`obj-shadow${onClick ? " obj-shadow--clickable" : ""}${className ? ` ${className}` : ""}`}
      style={style}
      onClick={onClick}
    >
      <span className="obj-shadow__halo" style={haloStyle} aria-hidden="true" />
      <span className="obj-shadow__content">{children}</span>
    </span>
  );
}

export default ObjectShadow;
